import TextField from '@/components/ui/text-field'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Accordion, AccordionBody, AccordionHeader, Button } from '@material-tailwind/react'
import { ChangePasswordSchema } from 'app/admin/utils/authValidation'
import { logOut, logOutAndClearReminder } from 'features/authSlice'
import { useAppDispatch } from 'features/hooks'
import { RootState } from 'features/store'
import { Formik } from 'formik'
import { useResetPasswordMutation } from 'graphql/generated/graphql'
import { useRouter } from 'next/navigation'
import React from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { ProfileSettingsOpenProp } from 'types/common'
import DeleteAccountDialog from './delete-account'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'


function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
}

const ProfileSecurity: React.FC<ProfileSettingsOpenProp> = ({ handleProfileSettingsOpen, deleteAccountModal, handleDeleteOpen }) => {
    const router = useRouter()
    const dispatch = useAppDispatch()
    const [open, setOpen] = React.useState(1);
    const user = useSelector((state: RootState) => state.culineerAuth.user)
    const [resetPassword, { loading: resetPasswordLoader }] = useResetPasswordMutation({ fetchPolicy: 'no-cache' })

    const handleOpen = (value) => setOpen(open === value ? 0 : value);
    
    return (
        <div>
            <Formik
                initialValues={{
                    oldPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                }}
                validationSchema={ChangePasswordSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                    const response = await resetPassword({
                        variables: {
                        input: {
                            email_address: user?.email_address,
                            currentPassword: values.oldPassword,
                            newPassword: values.newPassword,
                        },
                        },
                    })
                    if (response !== undefined) {
                        setSubmitting(false)
                        toast.success('Password changed successfully! Log in to continue.')
                        dispatch(logOutAndClearReminder())
                        handleProfileSettingsOpen()
                        return router.push('/auth/login')
                    }
                    } catch (error: any) {
                    setSubmitting(false)
                    return toast.error(error.message)
                    }
                }}
            >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit: handlePasswordChangeSubmit,
                isSubmitting,
            }) => (
                <form onSubmit={handlePasswordChangeSubmit}>
                <div
                    className={`${
                    errors.oldPassword && touched.oldPassword ? 'mb-4' : ''
                    }`}
                >
                    <TextField
                        inputType="password"
                        name={'oldPassword'}
                        placeholder="Current password"
                        label="Current password"
                        value={values.oldPassword}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        className={`h-12 placeholder:text-sm placeholder:text-gray-400 focus:text-sm !rounded-xl ${
                            errors.oldPassword && touched.oldPassword
                            ? 'border border-deep-orange-700'
                            : ''
                        }`}
                    />
                </div>
                <div
                    className={`${
                    errors.newPassword && touched.newPassword ? 'mb-4' : ''
                    }`}
                >
                    <TextField
                        inputType="password"
                        name={'newPassword'}
                        placeholder="New password"
                        label="New password"
                        value={values.newPassword}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        className={`h-12 placeholder:text-sm placeholder:text-gray-400 !rounded-xl ${
                            errors.newPassword && touched.newPassword
                            ? 'border border-deep-orange-700'
                            : ''
                        }`}
                    />
                </div>
                <div
                    className={`${
                    errors.confirmPassword && touched.confirmPassword
                        ? 'mb-4'
                        : ''
                    }`}
                >
                    <TextField
                        inputType="password"
                        name={'confirmPassword'}
                        placeholder="Confirm password"
                        value={values.confirmPassword}
                        label="Confirm password"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        className={`h-12 placeholder:text-sm placeholder:text-gray-400 !rounded-xl ${
                            errors.confirmPassword && touched.confirmPassword
                            ? 'border border-deep-orange-700'
                            : ''
                        }`}
                    />
                </div>
                <div className="flex items-center justify-end gap-4 pt-4">
                    <button type="button" onClick={handleProfileSettingsOpen} className='border px-6 py-2 rounded-full'>
                        Close
                    </button>
                    <Button
                        className="w-28 rounded-full bg-[#85C349] capitalize"
                        type="submit"
                        disabled={resetPasswordLoader || isSubmitting}
                        >
                        {resetPasswordLoader || isSubmitting ? (
                            <FontAwesomeIcon
                            icon={faSpinner}
                            className={' h-6 w-6 animate-spin text-white'}
                            />
                        ) : (
                            'Save'
                        )}
                    </Button>
                </div>
                </form>
            )}
            </Formik>
            <div className="border-t border-gray-400 mt-8">
                <Accordion open={open === 1} icon={<Icon id={1} open={open} />}>
                    <AccordionHeader className='text-lg border-none -mb-5' onClick={() => handleOpen(1)}>Manage Account</AccordionHeader>
                    <AccordionBody>
                        <p className="text-gray-600">Delete your account or manage your listings. All deletions have a 30-day recovery window where you can contact support to restore your data.</p>
                        <div className='pt-3'>
                            <button onClick={handleDeleteOpen} className='bg-red-50 text-red-700 border border-red-200 rounded-full px-8 py-2 text-sm hover:bg-red-100 transition-colors'>Manage deletion options</button>
                        </div>
                    </AccordionBody>
                </Accordion>
            </div>
            <Dialog open={deleteAccountModal} onOpenChange={handleDeleteOpen}>
                <DialogContent className="w-full max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-semibold">Are you sure?</DialogTitle>
                    </DialogHeader>
                    <DialogDescription />
                    <DeleteAccountDialog handleDeleteOpen={handleDeleteOpen} />
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default ProfileSecurity
